import { useState } from "react";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createLeadsBatch,
  getExecutiveEmployeeList,
} from "../../../actionCreator/leadListingActionCreater";
import { getUserInfo } from "../../../utils/utils";
import { isValidEmail, isValidMobile } from "../../../utils";
import { useAlert } from "../../../context/AlertProvider";

export const useLeadImport = (callback) => {
  const [selectedFile, setSelectedFile] = useState();
  const [leadData, setLeadData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const { id } = getUserInfo();

  const getCSVData = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const rows = content?.split("\n").map((row) => row.trim());
      const headers = rows[0]?.split(",");
      const data = rows?.slice(1).map((row) => {
        const values = row?.split(",");
        return headers.reduce((acc, header, index) => {
          acc[header.trim()] = values[index]?.trim();
          return acc;
        }, {});
      });
      setLeadData(data);
    };
    reader.readAsText(file);
  };

  const getExcelData = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryData = e.target.result;
      const workbook = XLSX.read(binaryData, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      setLeadData(jsonData);
    };
    reader.readAsBinaryString(file);
  };

  const onImportLeads = (info) => {
    setError();
    const file = info.file.originFileObj || info.file;
    setSelectedFile(file);
    const fileExtension = file?.name?.split(".").pop().toLowerCase();
    if (fileExtension === "csv") {
      getCSVData(file);
    } else if (["xls", "xlsx"].includes(fileExtension)) {
      getExcelData(file);
    } else {
      alert("Invalid file format. Please upload a .csv or .xlsx file.");
    }
  };

  const validateFileData = (data) => {
    const requiredFields = [
      "First Name",
      "Last Name",
      "Email",
      "Company Name",
      "Mobile",
    ];
    const errors = [];

    const fileHeaders = Object.keys(data[0]);
    const missingHeaders = requiredFields.filter(
      (field) => !fileHeaders.includes(field)
    );
    if (missingHeaders.length > 0) {
      errors.push(
        `Missing mandatory header${missingHeaders.length > 1 ? "s" : ""}: ${missingHeaders.join(", ")}`
      );
      return errors;
    }

    data.forEach((row, index) => {
      requiredFields.forEach((field) => {
        if (
          !row[field] ||
          (typeof row[field] === "string" && row[field].trim() === "")
        ) {
          errors.push(`Row ${index + 1}: ${field} is empty.`);
        }
      });
      if (row["Email"] && !isValidEmail(row["Email"])) {
        errors.push(`Row ${index + 1}: Email is not valid.`);
      }
      if (row["Mobile"] && !isValidMobile(row["Mobile"])) {
        errors.push(`Row ${index + 1}: Mobile is not a valid 10-digit number.`);
      }
    });

    return errors;
  };

  const refetchClientLeads = () => {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }

    dispatch(
      getExecutiveEmployeeList({
        ...allParams,
        page: allParams?.page ? allParams?.page - 1 : 0,
      })
    );
  };

  const onCreateLeads = async () => {
    setLoading(true);
    if (leadData?.length) {
      const errors = validateFileData(leadData);
      if (errors?.length > 0) {
        setError(errors);
      } else {
        const formattedLeads = leadData?.map((d) => ({
          firstname: d["First Name"],
          lastname: d["Last Name"],
          fullname: d["First Name"] + " " + d["Last Name"],
          title: d["Designation"],
          emailId: d["Email"],
          phoneNo: d["Phone"],
          address: d["Address"],
          website: d["Website"],
          companyName: d["Company Name"],
          industry: d["Industry"],
          leadSource: d["Lead Source"],
          mobile: d["Mobile"],
          description: d["Lead Description"],
          rate: d["Rate"],
          category: d["Category"],
          status: d["Status"],
          oppurtunityAmount: d["Opportunity Amount"],
          userId: id,
        }));
        const res = await dispatch(createLeadsBatch(formattedLeads));
        if (res === "error") {
          showAlert(
            "error",
            "Unable to complete the action. Please try again later."
          );
        } else {
          refetchClientLeads();
          showAlert("success", "Leads imported successfully.");
        }

        callback();
      }
    } else {
      setError(["Please upload a .csv or .xlsx file."]);
    }
    setLoading(false);
  };

  return {
    error,
    setError,
    selectedFile,
    setSelectedFile,
    loading,
    onImportLeads,
    onCreateLeads,
  };
};
